import { APIRequest, APIRequestMethod, APIRequestHeader } from "./APIRequest";
import { ClientCache } from "./ClientCache";
import { Config } from "../Config";
import { RUMAPICall } from "../RUM/RUMAPICall";
import { IRequestProgressDelegate } from "./IRequestProgressDelegate";
import { BrowserStorageService } from "../BrowserStorageService";
import { ServiceManager, ServiceType } from "../ServiceManager";
import { AuthToken } from "../../types/Auth";

interface MultipartResponse<T> {
  data?: T;
  error: boolean;
  statusCode: number;
}

interface RequestHeaders {
  [key: string]: string;
}

class Client {
    static Cache: ClientCache = new ClientCache();

    public static AuthToken: AuthToken | null = null;

    static SetAuthToken(token: AuthToken | null): void {
      try {
        const bss = ServiceManager.Get<BrowserStorageService>(ServiceType.BrowserStorage);

        if (bss) bss.setItem('prolist.api-credentials', JSON.stringify(token));
      } catch (ex) {
        console.error(ex);
      }

      this.AuthToken = token;
    }

    static XHR<T>(request: APIRequest, progressDelegate?: IRequestProgressDelegate): Promise<T> {
      return new Promise((resolve, reject) => {
        let body: string | FormData | undefined = undefined;

        const url = Client.BuildRequestUrl(request);

        if (request.Method === APIRequestMethod.POST && request.Data !== undefined) {
          if(request.Data instanceof FormData) {
            body = request.Data;
          } else {
            body = JSON.stringify(request.Data);
            request.Headers.push(new APIRequestHeader("content-type", "application/json"));
          }
        }

        const headers = Client.BuildRequestHeaders(request);

        const xhr = new XMLHttpRequest();

        xhr.open(request.Method.toString(), url, true);

        Object.keys(headers).forEach((header) => {
          xhr.setRequestHeader(header, headers[header]);
        });

        xhr.onreadystatechange = (ev) => {
          if (xhr.readyState === XMLHttpRequest.DONE && ((xhr.status >= 200 && xhr.status <= 399) || xhr.status === 0)) {
            if (xhr.responseText.length) {
              resolve(JSON.parse(xhr.responseText));
              return;
            }
          }
          
          if (xhr.readyState === XMLHttpRequest.DONE) {
            reject();
            return;
          }
        }

        if (progressDelegate !== undefined) {
          xhr.onprogress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
              progressDelegate.onProgress(e.loaded, e.total);
            }
          }
        }

        xhr.send(body);
      });
    }

    static BuildRequestHeaders(req: APIRequest): RequestHeaders {
      const headers: RequestHeaders = {};

      req.Headers.forEach((header) => {
        headers[header.Key] = header.Value;
      });

      if(req.Host === Config.API.Base) {
        headers["x-prolist-website-id"] = Config.API.WebsiteId;
        headers["x-prolist-website-level"] = Config.API.WebsiteLevel.toString();
        headers["x-prolist-client-website-id"] = Config.Website.Id;

        // Check to see if we have an auth token stores
        if (this.AuthToken !== null) {
          headers["authorization"] = `Bearer ${this.AuthToken.Header}:${this.AuthToken.Token}`;
        }
      }

      return headers;
    }

    static BuildRequestUrl(req: APIRequest): string {
      const query = req.Params.map((p) => `${encodeURIComponent(p.Key)}=${encodeURIComponent(p.Value)}`).join('&');

      return req.Host + req.URL + (query.length === 0 ? '' : `?${query}`);
    }

    static BuildFetchReq(req: APIRequest) : Request {
      var body: any = null;

      if(req.Method == APIRequestMethod.POST) {
        if(req.Data instanceof FormData) {
          body = req.Data;
        } else {
          req.Headers.push(new APIRequestHeader("content-type", "application/json"));
          body = JSON.stringify(req.Data);
        }
      }

      const headers = Client.BuildRequestHeaders(req);
      const url = Client.BuildRequestUrl(req);

      var fetchReq = new Request(url, {
        method: req.Method.toString(),
        mode: "cors",
        cache: req.CacheMode.toString(),
        headers: headers,
        body: body
      } as RequestInit);

      return fetchReq;
    }

    static Fetch(request: Request) : Promise<Response> {
      return new Promise((resolve, reject) => {
        this.Cache.Get(request).then(res => {
          //If we have a cached value, resolve with it
          if(res) {
            resolve(res);
            return;
          }

          //Make the actual HTTP Request
          window.fetch(request).then(response => {
            //Cache the resulting data
            if (request.method == 'GET') this.Cache.Put(request, response.clone());

            //Resolve
            resolve(response);
          }).catch(err => {
            reject(err);
          });
        }).catch(err => {
          reject(err);
        });
      });
    }

    static RawRequest(request: APIRequest) : Promise<Response> {
      return new Promise((resolve, reject) => {
        //Build the Fetch params
        let req = this.BuildFetchReq(request);

        let rumData: RUMAPICall | null = null;
        if(request.RUMEnabled) rumData = new RUMAPICall(req.url, req.method);

        this.Fetch(req).then(res => {
          
          if(res.ok) {
              resolve(res);

              if (rumData) rumData.SetComplete(res.status);
          } else {
              reject(res);

              if (rumData) rumData.SetErrored(res.status);
          }

        }).catch(err => {
          reject(err);

          if (rumData) rumData.SetErrored(-1);
        });
      });
    }

    static Request<T>(request: APIRequest): Promise<T> {
      return this.RawRequest(request)
        .then(res => res.json())
        .then(data => new Promise((r) => r(data as T)));
    }
}

export { Client }
